import { CommonModule, ViewportScroller } from '@angular/common';
import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewContainerRef,
    inject,
} from '@angular/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, NavigationExtras, Router, RouterModule, Scroll } from '@angular/router';
import { ProductUid } from '@constants';
import {
    AnalyticsService,
    KeplerSfdcService,
    ProductQueryParamService,
    ProductService,
    RouteService,
    ToastNotificationService,
    UserService,
} from '@services';
import { lockBackgroundScrolling, scrollToTopScrolling, unlockBackgroundScrolling } from '@utils';
import {
    BehaviorSubject,
    EMPTY,
    Observable,
    Subscription,
    catchError,
    combineLatest,
    debounceTime,
    filter,
    map,
    of,
    switchMap,
    take,
    tap,
} from 'rxjs';
import { AppFooterComponent } from './app-footer/app-footer.component';
import { AppHeaderProductSelectService } from './app-header/app-header-product-select.service';
import { AppHeaderComponent } from './app-header/app-header.component';
import { SupportOutageBannerComponent } from './app-header/support-outage-banner/support-outage-banner.component';
import { SupportOutageBannerService } from './app-header/support-outage-banner/support-outage-banner.service';
import { AppIconRegistryService } from './app-icon-registry.service';
import { ChildPageRoute, GknRoutePath } from './app-routing.enum';
import { BackToTopComponent } from './back-to-top/back-to-top.component';

@Component({
    selector: 'app-root',
    imports: [
        CommonModule,
        AppHeaderComponent,
        AppFooterComponent,
        BackToTopComponent,
        MatIconModule,
        RouterModule,
        MatDialogModule,
        SupportOutageBannerComponent,
    ],
    providers: [
        {
            provide: MatDialogRef,
            useValue: {},
        },
        AppHeaderProductSelectService,
    ],
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
    private analyticsService = inject(AnalyticsService);
    private iconRegistryService = inject(AppIconRegistryService);
    private keplerSfdcService = inject(KeplerSfdcService);
    private productService = inject(ProductService);
    private productQueryParamService = inject(ProductQueryParamService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private routeService = inject(RouteService);
    private titleService = inject(Title);
    private notificationService = inject(ToastNotificationService);
    private userService = inject(UserService);
    private viewRef = inject(ViewContainerRef);
    private viewportScroller = inject(ViewportScroller);
    private bannerService = inject(SupportOutageBannerService);

    private subs: Subscription[] = [];

    isSearchPage$: Observable<boolean>;
    isSupportPage$: Observable<boolean>;
    showHeroBanner$: Observable<boolean>;
    showAppHeader$: Observable<boolean>;
    showAppFooter$: Observable<boolean>;
    hasFragment = false;

    isAuthenticated$: Observable<boolean>;
    bannerVisible$ = new BehaviorSubject(false);

    /** Container element to animate the opening/closing of the profile "component", not "page", in mobile */
    @ViewChild('mobileProfileContainer') mobileProfileContainer: ElementRef;

    /** top margin for the footer in pixels. this is consistent across all Figma designs. under special circumstances, this value can change, like a custom page */
    footerMarginTopPx$: Observable<number>;

    constructor() {
        this.notificationService.viewRef = this.viewRef;
    }

    ngOnInit(): void {
        // TODO - Update after orchestrators release. For now just leave the title the same
        // if (environment.name != 'prod') {
        //     // Set the browser tab title for dev quality of life.
        //     this.titleService.setTitle(`GKN | ${environment.gitInfo.branch}`);
        //     console.debug(environment.gitInfo);
        // }

        this.analyticsService.initialize();
        this.iconRegistryService.start();

        this.isAuthenticated$ = this.userService.isAuthenticated$;

        // Claims imply successful login, thus initialize Salesforce and Pendo with the user's email
        this.subs.push(
            this.userService.isAuthenticated$
                .pipe(
                    filter((isAuthed) => isAuthed == true),
                    debounceTime(3000), // delay starting analytics for 3 seconds. This avoids races with initializing the sfdc user via UserService
                    switchMap(() => {
                        return this.analyticsService.identify();
                    }),
                    catchError((err) => {
                        console.error(err);
                        return EMPTY; // complete with no emission
                    }),
                )
                .subscribe(),
        );

        this.isSearchPage$ = this.routeService.currentRoute$.pipe(
            map((currentRoute) => currentRoute === GknRoutePath.SEARCH),
        );

        this.observeProductQueryParamChanges();

        this.showHeroBanner$ = this.routeService.currentRoute$.pipe(
            map((route) => route != GknRoutePath.SEARCH && route != GknRoutePath.SIGNUP),
        );

        this.showAppHeader$ = this.routeService.currentRoute$.pipe(
            map((route) => route != GknRoutePath.DIGITAL_ASSISTANT && route != GknRoutePath.EDUCATION_SUPPORT),
        );

        this.showAppFooter$ = this.routeService.currentRoute$.pipe(
            map((route) => route != GknRoutePath.DIGITAL_ASSISTANT && route != GknRoutePath.EDUCATION_SUPPORT),
        );

        this.footerMarginTopPx$ = combineLatest({
            route: this.routeService.currentRoute$,
            children: this.routeService.currentRouteChildren$,
        }).pipe(
            map(({ route, children }) =>
                route == GknRoutePath.SSO_CALLBACK ||
                (route == GknRoutePath.BILLING && children?.includes(ChildPageRoute.BILLING_OAUTH_CALLBACK)) ||
                (route == GknRoutePath.RENEWAL && children?.length == 0) ||
                (route == GknRoutePath.RENEWAL && children?.includes(ChildPageRoute.RENEWAL_THANK_YOU))
                    ? 0
                    : 82,
            ),
        );

        this.subs.push(
            this.router.events
                .pipe(
                    filter((event) => event instanceof NavigationEnd),
                    switchMap((event: NavigationEnd) => {
                        const url: string = event.urlAfterRedirects;

                        if (url.includes('#')) {
                            this.hasFragment = true;
                        }

                        if (url && url != '/') {
                            if (!this.route.snapshot.queryParams['product']) {
                                console.debug('no product query param. setting to subscribed product');
                                return this.keplerSfdcService.subscribedProducts$.pipe(
                                    take(1),
                                    tap((products) => {
                                        let queryParam = ProductUid.GENESYS_CLOUD_CX;
                                        if (products.length) {
                                            queryParam = products[0].uid;
                                        }
                                        const navigationExtras: NavigationExtras = {
                                            queryParams: { product: queryParam },
                                            queryParamsHandling: 'merge',
                                            preserveFragment: true,
                                        };
                                        this.router.navigate([], navigationExtras);
                                    }),
                                );
                            }
                        }
                        return of(null);
                    }),
                )
                .subscribe(),
        );

        this.isSupportPage$ = this.routeService.currentRoute$.pipe(
            map((currentRoute) => currentRoute === GknRoutePath.SUPPORT),
        );

        this.subs.push(
            this.isSupportPage$.subscribe((isSupportPage) => {
                if (!isSupportPage) this.bannerVisible$.next(false);
            }),
        );
    }

    /**
     * Detects browser query parameter changes specific to the `product` key,
     * then subsequently sets the selected product in the app.
     *
     * This function has several flows and is "recursive" in nature.
     * - If a query param is not present, one will be set (to either the user's
     * default product or to Genesys Cloud). Once that parameter is set, this
     * method then detects that and sets the selected product.
     * - If a query param is present, then it is first validated before setting
     * the selected product.
     *
     * Enable trace logging in your browser to follow changes within this function
     */
    private observeProductQueryParamChanges(): void {
        this.subs.push(
            combineLatest([
                this.productQueryParamService.productQueryParam$,
                this.keplerSfdcService.subscribedProducts$, // subscribed products > 0 implies that this is an auth'ed user
            ]).subscribe(([productQueryParam, subscribedProducts]) => {
                console.debug('app component detected product query param', productQueryParam);
                if (productQueryParam) {
                    // NOTE: Exclude any deprecated Product Uids from the product url parameter
                    const isProductQueryParamValid = Object.values(ProductUid).some((uid) => uid === productQueryParam);

                    if (isProductQueryParamValid) {
                        console.debug(
                            `valid product query param present, emitting ${productQueryParam} throughout app`,
                        );
                        // This is the only condition in which we should set the selected product!
                        this.productService.setSelectedProduct(productQueryParam);
                    } else {
                        console.debug(
                            `invalid product query param ${productQueryParam} replaced with ${ProductUid.GENESYS_CLOUD_CX}`,
                        );
                        this.productQueryParamService.setProductQueryParam(ProductUid.GENESYS_CLOUD_CX);
                    }
                } else if (subscribedProducts.length > 0) {
                    const defaultProductUid: ProductUid = subscribedProducts[0].uid;
                    this.productQueryParamService.setProductQueryParam(defaultProductUid);
                } else {
                    console.debug(`undefined product query param replaced with ${ProductUid.GENESYS_CLOUD_CX}`);
                    this.productQueryParamService.setProductQueryParam(ProductUid.GENESYS_CLOUD_CX);
                }
            }),
        );
    }

    ngAfterViewInit() {
        this.subs.push(
            this.router.events
                .pipe(
                    filter((e): e is Scroll => e instanceof Scroll),
                    tap((e1) => {
                        const e = e1 as Scroll;
                        if (e.anchor) {
                            // anchor navigation
                            this.viewportScroller.setOffset([0, 228]); // 158px is the height of the header + 70px for the height of the section header
                            setTimeout(() => {
                                this.viewportScroller.scrollToAnchor(e.anchor);
                            }, 500);
                        } else if (e.position) {
                            // backward navigation
                            this.viewportScroller.scrollToPosition(e.position);
                        } else {
                            // forward navigation
                            this.viewportScroller.scrollToPosition([0, 0]);
                        }
                    }),
                )
                .subscribe(),
        );
    }

    onBannerStateChange(isVisible: boolean): void {
        this.bannerVisible$.next(isVisible);
        // DO NOT DELETE: Sets banner height when the support outage banner is shown or removed
        document.documentElement.style.setProperty('--outage-banner-height', isVisible ? '65px' : '0px');
    }

    /**
     * Scrolls the window to the top of the page.
     */
    scrollToTop(): void {
        if (!this.hasFragment) scrollToTopScrolling();
    }

    openMobileProfile(): void {
        lockBackgroundScrolling();
        this.mobileProfileContainer?.nativeElement.classList.add('active');
    }

    closeMobileProfile(): void {
        unlockBackgroundScrolling();
        this.mobileProfileContainer?.nativeElement.classList.remove('active');
    }

    ngOnDestroy(): void {
        this.subs?.forEach((sub) => sub?.unsubscribe());
    }
}
